import Matter from 'matter-js';
import { api } from '../api.js';
import { createSurface, resizeToFullscreen } from '../../render/canvas.js';
import { createPhysicsWorld, wakeAllBodies } from '../../physics/engine.js';
import { drawLetters } from '../../letters/drawLetters.js';
import { dropString, spawnMessageBodies } from '../../letters/dropString.js';
import {
  findLetterAt,
  startRevive,
  startDismiss,
  stepRevive,
  breakReadOnlyHold,
} from '../../letters/reviveString.js';
import { getLetterPlugin, isLetterBody } from '../../physics/letterBody.js';
import { initTypingBox } from '../../input/typingBox.js';
import { state, type TypedString } from '../../state.js';
import type { JarRecord } from '../../shared/contracts.js';

// `(hover: none)` matches touch-primary devices (phones, touch-only tablets)
// and miss-matches on laptops with a mouse/trackpad. Use it to swap the
// hint copy between mouse+keyboard and tap-only idioms. Captured at module
// load — good enough since a device's primary input doesn't change session
// to session.
const IS_TOUCH = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

const HINT_INITIAL = IS_TOUCH
  ? 'Type a note and tap Return to drop it in.'
  : 'Write a note and press Enter to drop it in.';
const HINT_AFTER_DROP = IS_TOUCH
  ? 'Double-tap a letter to revive a note.'
  : 'Double-click a letter to revive a note.';
const HINT_READONLY = IS_TOUCH
  ? 'Double-tap to break it apart.'
  : 'Enter to let it fall · Esc to put it back.';
const ATTRIBUTION_HINT = IS_TOUCH ? '↵ to send' : '↵ to send · esc to edit';
const MAX_NOTE_CHARS = 1500;
const MAX_NAME_CHARS = 50;
const OVER_CAP_MSG = `Your note exceeds the ${MAX_NOTE_CHARS.toLocaleString()} letter cap. Please shorten it.`;

type StoredAttribution = { name: string; anonymous: boolean };

function attributionStorageKey(jarName: string): string {
  return `textjar.attr.${jarName}`;
}

function readStoredAttribution(jarName: string): StoredAttribution | null {
  try {
    const raw = window.localStorage.getItem(attributionStorageKey(jarName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      anonymous: !!parsed.anonymous,
    };
  } catch {
    return null;
  }
}

function writeStoredAttribution(jarName: string, value: StoredAttribution): void {
  try {
    window.localStorage.setItem(attributionStorageKey(jarName), JSON.stringify(value));
  } catch {
    /* private-browsing or quota — fall back to session-only behavior */
  }
}

function emailLocalPart(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

function navigate(path: string): void {
  history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderNotFound(root: HTMLElement): void {
  root.innerHTML = `
    <section class="card">
      <h1>Jar not found</h1>
      <p class="muted">This page might not exist yet or is private.</p>
      <p><a href="/" data-nav="internal">Back to homepage</a></p>
    </section>
  `;
  root.querySelector('a[data-nav="internal"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    navigate('/');
  });
}

export async function renderPublicJarView(root: HTMLElement, name: string): Promise<void> {
  let jar: JarRecord | null = null;
  try {
    jar = await api.getJarByName(name);
  } catch {
    renderNotFound(root);
    return;
  }
  if (!jar) {
    renderNotFound(root);
    return;
  }
  // Private jars are viewable by their owner. RLS already gates `getJarByName`
  // to public-or-owner rows, but a fresh auth check avoids a stale cache
  // leaking a private jar to a viewer who logged out in another tab.
  if (!jar.isPublic) {
    let ownerMatch = false;
    try {
      const me = await api.me();
      ownerMatch = !!me && me.id === jar.ownerId;
    } catch {
      ownerMatch = false;
    }
    if (!ownerMatch) {
      renderNotFound(root);
      return;
    }
  }

  const description = jar.description?.trim() ?? '';
  const subtitle = description ? `<p class="muted">${escapeHtml(description)}</p>` : '';
  root.innerHTML = `
    <div class="jar-stage">
      <header class="jar-header">
        <a href="/" data-nav="internal" class="jar-header-back">← jars</a>
        <div class="jar-header-title">
          <h1>${escapeHtml(jar.title)}</h1>
          ${subtitle}
        </div>
      </header>
      <canvas id="jar-canvas"></canvas>
      <div id="jar-letter-count" class="jar-letter-count" aria-hidden="true">0</div>
      <div class="typing-wrap">
        <div id="typing-box" class="typing-box" contenteditable="true" spellcheck="false" data-placeholder="type here…"></div>
        <div id="jar-attribution" class="jar-attribution" aria-hidden="true">
          <span class="jar-attribution-label">from</span>
          <input id="jar-attribution-name" class="jar-attribution-name" type="text" maxlength="${MAX_NAME_CHARS}" autocomplete="off" spellcheck="false" placeholder="your name" />
          <label class="jar-attribution-anon">
            anonymous
            <input type="checkbox" id="jar-attribution-anon-toggle" />
          </label>
          <span class="jar-attribution-hint">${ATTRIBUTION_HINT}</span>
        </div>
        <div id="jar-revived-date" aria-hidden="true"></div>
      </div>
      <div id="jar-hint" class="jar-hint" aria-hidden="true"></div>
      <p id="jar-toast" class="jar-toast" role="status"></p>
    </div>
  `;

  root.querySelector('a[data-nav="internal"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    navigate('/');
  });

  const canvasEl = root.querySelector<HTMLCanvasElement>('#jar-canvas')!;
  const typingWrapEl = root.querySelector<HTMLElement>('.typing-wrap')!;
  const typingEl = root.querySelector<HTMLElement>('#typing-box')!;
  const toastEl = root.querySelector<HTMLElement>('#jar-toast')!;
  const hintEl = root.querySelector<HTMLElement>('#jar-hint')!;
  const revivedDateEl = root.querySelector<HTMLElement>('#jar-revived-date')!;
  const letterCountEl = root.querySelector<HTMLElement>('#jar-letter-count')!;
  const attributionEl = root.querySelector<HTMLElement>('#jar-attribution')!;
  const attributionNameEl = root.querySelector<HTMLInputElement>('#jar-attribution-name')!;
  const attributionAnonEl = root.querySelector<HTMLInputElement>('#jar-attribution-anon-toggle')!;

  // On mobile the software keyboard shrinks `visualViewport` without changing
  // `window.innerHeight`. Lift the typing wrap by half the difference so it
  // re-centers in the visible area instead of hiding behind the keyboard. On
  // desktop visualViewport.height === innerHeight, so lift stays 0.
  const stopKeyboardAvoidance = (() => {
    const vv = window.visualViewport;
    if (!vv) return () => {};
    const update = () => {
      const lift = Math.max(0, (window.innerHeight - vv.height) / 2);
      typingWrapEl.style.setProperty('--kb-lift', `${lift}px`);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  })();

  const surface = createSurface(canvasEl);
  const world = createPhysicsWorld(window.innerWidth, window.innerHeight);
  resizeToFullscreen(surface, () => world.resize(surface.width, surface.height));

  const mouse = Matter.Mouse.create(canvasEl);
  mouse.pixelRatio = window.devicePixelRatio || 1;
  const mouseConstraint = Matter.MouseConstraint.create(world.engine, {
    mouse,
    constraint: { stiffness: 0.22, damping: 0.1, render: { visible: false } },
  });
  Matter.World.add(world.engine.world, mouseConstraint);
  Matter.Events.on(mouseConstraint, 'startdrag', (event) => {
    const body = (event as unknown as { body?: Matter.Body }).body;
    if (body && !isLetterBody(body)) {
      mouseConstraint.constraint.bodyB = null;
      return;
    }
    // Yanking a letter out of the pile leaves sleeping former-neighbors
    // hanging — wake the whole pile so gravity pulls them down.
    wakeAllBodies(world.engine);
  });

  let hintTimeout: number | undefined;
  function showHint(msg: string, durationMs = 2600): void {
    hintEl.textContent = msg;
    if (hintTimeout) clearTimeout(hintTimeout);
    hintTimeout = window.setTimeout(() => {
      hintEl.textContent = '';
    }, durationMs);
  }
  function showRevivedDate(str: TypedString): void {
    const d = new Date(str.createdAt);
    const datePart = d.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const name = str.senderName && str.senderName.trim() ? str.senderName.trim() : 'Anonymous';
    // textContent escapes — safe to interpolate user-controlled name directly.
    revivedDateEl.textContent = `from ${name} · ${datePart} · ${timePart}`;
    revivedDateEl.classList.add('visible');
  }
  function hideRevivedDate(): void {
    revivedDateEl.classList.remove('visible');
  }

  function showToast(msg: string, durationMs = 2800): void {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    window.setTimeout(() => toastEl.classList.remove('show'), durationMs);
  }

  // Grab the email once so the attribution row can pre-fill the name input
  // with the local-part when there's no stored per-jar default yet.
  let myEmail: string | null = null;
  try {
    const me = await api.me();
    myEmail = me?.email ?? null;
  } catch {
    // Treat auth errors as signed-out — no pre-filled name, posts still work.
  }

  // Attribution flow:
  //   - First post this session: open the attribution row so the user can
  //     choose a display name or toggle "anonymous", then Enter commits.
  //   - Subsequent posts this session: silently reuse the stored choice —
  //     the user isn't re-prompted.
  //   - localStorage only seeds the first-prompt default across page loads.
  let attributing = false;
  let submitting = false;
  let sessionAttribution: { name: string; anonymous: boolean } | null = null;
  // The last name the user typed before checking "anonymous" — restored to the
  // input when they untoggle so they don't have to re-type it.
  let savedName = '';

  // Skip the attribution prompt in two cases:
  //   1. Private jars — only the owner can see/post, so there's no audience
  //      to attribute to. Auto-use the owner's email local-part as the name.
  //   2. Public jars where this browser has a stored attribution for this
  //      jar already — the user has answered the prompt in a previous visit,
  //      don't ask again on refresh.
  if (!jar!.isPublic) {
    sessionAttribution = { name: emailLocalPart(myEmail), anonymous: false };
  } else {
    const stored = readStoredAttribution(jar!.name);
    if (stored) sessionAttribution = stored;
  }

  function applyAnonState(): void {
    const anon = attributionAnonEl.checked;
    if (anon) {
      // Only stash when transitioning in — don't capture the literal
      // "Anonymous" label if applyAnonState is called twice in a row.
      if (!attributionNameEl.disabled) {
        savedName = attributionNameEl.value;
      }
      attributionNameEl.value = 'Anonymous';
      attributionNameEl.disabled = true;
      attributionNameEl.classList.add('is-anon');
    } else {
      attributionNameEl.value = savedName;
      attributionNameEl.disabled = false;
      attributionNameEl.classList.remove('is-anon');
    }
  }
  attributionAnonEl.addEventListener('change', applyAnonState);

  function resolveSenderNameFromChoice(choice: { name: string; anonymous: boolean }): string | null {
    if (choice.anonymous) return null;
    const trimmed = choice.name.trim();
    return trimmed || null;
  }

  function readAttributionUI(): { name: string; anonymous: boolean } {
    const anonymous = attributionAnonEl.checked;
    // When anon is on, the input shows the literal word "Anonymous" — use the
    // stashed pre-toggle name so we persist what the user actually typed.
    const name = (anonymous ? savedName : attributionNameEl.value).trim();
    return { name, anonymous };
  }

  function openAttribution(): void {
    const stored = readStoredAttribution(jar!.name);
    let defaultName: string;
    let defaultAnon: boolean;
    if (stored) {
      defaultName = stored.name;
      defaultAnon = stored.anonymous;
    } else {
      defaultName = emailLocalPart(myEmail);
      defaultAnon = false;
    }
    // Reset the input to editable state so applyAnonState can transition
    // from a known baseline, regardless of what a previous open left behind.
    attributionNameEl.disabled = false;
    attributionNameEl.classList.remove('is-anon');
    attributionNameEl.value = defaultName;
    savedName = defaultName;
    attributionAnonEl.checked = defaultAnon;
    applyAnonState();
    attributing = true;
    attributionEl.setAttribute('data-open', 'true');
    attributionEl.setAttribute('aria-hidden', 'false');
    // Keep the typing box editable and un-dimmed during the attribution step
    // so the user can still read + edit the message they're about to send.
    // Enter from either the typing box or the name input will commit.
    attributionNameEl.focus();
    attributionNameEl.select();
  }

  function closeAttribution(refocusTypingBox: boolean): void {
    attributing = false;
    attributionEl.removeAttribute('data-open');
    attributionEl.setAttribute('aria-hidden', 'true');
    if (refocusTypingBox) {
      typingEl.focus();
      // Move caret to end so the user can keep typing after an Esc cancel.
      const range = document.createRange();
      range.selectNodeContents(typingEl);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }

  // Single source of truth for posting + dropping letters. Callers decide
  // whether the typing box's DOM is cleared by them (attribution path) or by
  // `typingBox.ts` (silent path returns undefined from onSubmit).
  async function commitPost(text: string, senderName: string | null): Promise<boolean> {
    if (submitting) return false;
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (trimmed.length > MAX_NOTE_CHARS) {
      showToast(OVER_CAP_MSG, 3600);
      return false;
    }
    submitting = true;
    let insertedMeta: { id: string; userId: string | null; createdAt: string } | null = null;
    try {
      insertedMeta = await api.postMessage(name, { body: text, senderName });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not save your message.');
      submitting = false;
      return false;
    }
    submitting = false;

    const dropped = dropString(typingEl, world.engine);
    if (!dropped) return true;
    const ts = state.strings.get(dropped.stringId);
    if (ts) {
      ts.sessionOwned = true;
      ts.senderName = senderName;
      if (insertedMeta) {
        ts.messageId = insertedMeta.id;
        ts.authorUserId = insertedMeta.userId;
      }
    }
    hideRevivedDate();
    showHint(HINT_AFTER_DROP, 3500);
    return true;
  }

  async function commitFromAttribution(): Promise<void> {
    const text = typingEl.innerText;
    if (!text.trim()) {
      closeAttribution(true);
      return;
    }
    const choice = readAttributionUI();
    const senderName = resolveSenderNameFromChoice(choice);
    const ok = await commitPost(text, senderName);
    if (!ok) return; // leave attribution row open for retry
    sessionAttribution = choice;
    writeStoredAttribution(jar!.name, choice);
    typingEl.innerHTML = '';
    closeAttribution(true);
  }

  attributionEl.addEventListener('keydown', (e) => {
    if (!attributing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      void commitFromAttribution();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAttribution(true);
    }
  });

  typingEl.addEventListener('input', () => {
    hideRevivedDate();
  });

  // Shared exit path for a read-only held note. Triggered by Enter (desktop),
  // double-click on the canvas (desktop), or double-tap on a revived letter
  // (mobile — no Enter key surfaced because the typing box is hidden).
  function exitReadOnlyHold(): void {
    breakReadOnlyHold(world.engine);
    typingEl.classList.remove('reviving');
    typingEl.innerHTML = '';
    hideRevivedDate();
  }

  const typingBox = initTypingBox(typingEl, {
    onSubmit: async (text) => {
      if (submitting) return false;
      // Read-only hold + Enter = "break it apart": unfreeze the held letters
      // so gravity pulls them down into the pile and the typing stage resets.
      if (state.readOnlyHold) {
        exitReadOnlyHold();
        return; // lets typingBox clear the (invisible) note text
      }
      // Attribution row is open — Enter from the typing box commits the post
      // using whatever is currently in the name input + anonymous toggle. The
      // typing box stays editable during attribution, so the user might be
      // pressing Enter from the message area itself.
      if (attributing) {
        await commitFromAttribution();
        return false; // commitFromAttribution clears the DOM itself on success
      }
      if (text.trim().length > MAX_NOTE_CHARS) {
        showToast(OVER_CAP_MSG, 3600);
        return false;
      }

      // Already picked attribution in this session — post silently and let
      // typingBox clear the DOM on our non-false return.
      if (sessionAttribution) {
        const senderName = resolveSenderNameFromChoice(sessionAttribution);
        const ok = await commitPost(text, senderName);
        return ok ? undefined : false;
      }

      // First post this session — open the attribution row for the one prompt.
      openAttribution();
      return false;
    },
  });

  function tryRevive(clientX: number, clientY: number): void {
    if (state.revive) return;
    // Already holding a read-only note open. Esc dismisses it first.
    if (state.readOnlyHold) return;
    const body = findLetterAt(world.engine, { x: clientX, y: clientY });
    if (!body) return;
    const plugin = getLetterPlugin(body);
    if (!plugin) return;

    mouseConstraint.constraint.bodyB = null;

    // Reviving cancels any in-progress attribution for the draft currently
    // in the typing box and abandons any typed-but-undropped text (the draft
    // falls into the pile as a non-persisted local drop).
    if (attributing) closeAttribution(false);
    if (typingEl.innerText.trim()) {
      dropString(typingEl, world.engine);
      typingEl.innerHTML = '';
    }

    const str = state.strings.get(plugin.stringId);
    if (!str) return;

    typingEl.innerText = str.text;
    typingEl.classList.add('reviving');
    // Force layout so reviveString can probe target rects.
    void typingEl.clientHeight;

    // Messages are immutable after posting — every revive is read-only.
    const started = startRevive(world.engine, plugin.stringId, typingEl, { readOnly: true });
    if (!started) {
      typingEl.classList.remove('reviving');
      typingEl.innerHTML = '';
      return;
    }
    showHint(HINT_READONLY, 4200);
  }

  canvasEl.addEventListener('dblclick', (e) => {
    // While a note is held read-only, double-clicking breaks it apart (same
    // effect as Enter). Gives desktop users a pointer-only way to release.
    if (state.readOnlyHold) {
      exitReadOnlyHold();
      return;
    }
    tryRevive(e.clientX, e.clientY);
  });

  // Double-tap detection on mobile — MouseConstraint's 'mousedown' fires for
  // touch too, and exposes the hit body via mouseConstraint.body.
  let lastTapBody: number | null = null;
  let lastTapTime = 0;
  const DOUBLE_TAP_MS = 320;
  Matter.Events.on(mouseConstraint, 'mousedown', () => {
    const body = mouseConstraint.body;
    if (!body || !isLetterBody(body)) {
      lastTapBody = null;
      return;
    }
    const now = performance.now();
    if (lastTapBody === body.id && now - lastTapTime < DOUBLE_TAP_MS) {
      lastTapBody = null;
      // On mobile the typing box is hidden during readOnlyHold, so Enter isn't
      // reachable. Double-tapping any revived letter breaks the note apart —
      // the mobile equivalent of pressing Enter.
      if (state.readOnlyHold) {
        exitReadOnlyHold();
        return;
      }
      const pos = mouse.position;
      tryRevive(pos.x, pos.y);
    } else {
      lastTapBody = body.id;
      lastTapTime = now;
    }
  });

  let stopped = false;
  let last = performance.now();
  let lastLetterCount = -1;
  const step = (now: number) => {
    if (stopped) return;
    const dt = Math.min(32, now - last);
    last = now;
    Matter.Engine.update(world.engine, dt);

    if (state.revive) {
      const done = stepRevive(world.engine, now);
      if (done) {
        const { stringId, reverse: wasReverse } = state.revive;
        state.revive = null;
        const str = state.strings.get(stringId);

        if (wasReverse) {
          // Read-only note returned to the pile. Reset the typing-box surface.
          typingEl.classList.remove('reviving');
          typingEl.innerHTML = '';
          hideRevivedDate();
          typingBox.focus();
        } else if (str) {
          // Letters now sit static at the typing-box positions and spell the
          // note out in the canvas. Keep `.reviving` so the DOM text stays
          // hidden; the user presses Esc to animate them back (or Enter to
          // break them apart into the pile).
          showRevivedDate(str);
        } else {
          typingEl.classList.remove('reviving');
          typingEl.innerHTML = '';
        }
      }
    }

    surface.clear('transparent');
    drawLetters(surface, world.engine);

    let count = 0;
    for (const b of Matter.Composite.allBodies(world.engine.world)) {
      if (isLetterBody(b)) count++;
    }
    if (count !== lastLetterCount) {
      letterCountEl.textContent = count.toLocaleString();
      lastLetterCount = count;
    }

    requestAnimationFrame(step);
  };

  const stageEl = root.querySelector<HTMLElement>('.jar-stage')!;
  const onKey = (e: KeyboardEvent) => {
    // During attribution the typing box stays editable, so Esc might be
    // pressed with typing-box focus (not the attribution row). Handle that
    // here so the cancel gesture works regardless of where focus is.
    // Enter-while-attributing is handled by typingBox.ts's onSubmit (typing
    // box focus) and attributionEl's own keydown listener (name-input focus).
    if (attributing) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAttribution(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      // Esc first dismisses a read-only note back into the pile — only if
      // nothing else is unhidden by the same keystroke.
      if (state.readOnlyHold && !state.revive) {
        e.preventDefault();
        startDismiss(world.engine, state.readOnlyHold.stringId);
        return;
      }
      e.preventDefault();
      stageEl.classList.toggle('ui-hidden');
      return;
    }
    // Enter during a read-only hold breaks the note apart. The typing box
    // is `visibility: hidden` while .reviving is applied, so it can't receive
    // focus — catch Enter at window level.
    if (e.key === 'Enter' && state.readOnlyHold && !state.revive) {
      e.preventDefault();
      exitReadOnlyHold();
      typingBox.focus();
    }
  };
  window.addEventListener('keydown', onKey);

  const onPop = () => {
    stopped = true;
    window.removeEventListener('popstate', onPop);
    window.removeEventListener('keydown', onKey);
    stopKeyboardAvoidance();
  };
  window.addEventListener('popstate', onPop);

  // Wait for the custom font so the first letters render in 'Henry', not a fallback.
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
  if (stopped) return;

  // Start the physics loop before hydrating so letters actually fall as we
  // spawn them one-by-one.
  requestAnimationFrame(step);

  // Hydrate existing messages from the database so every visitor sees the
  // same pile. During hydration the typing box is disabled and invisible
  // (via the `.reviving` class) so we can reuse its layout to probe rects.
  try {
    const msgs = await api.listJarMessages(jar.id);
    if (!stopped && msgs.length > 0) {
      typingEl.contentEditable = 'false';
      typingEl.classList.add('reviving');
      const gapMs = msgs.length > 30 ? 40 : 90;
      for (const msg of msgs) {
        if (stopped) break;
        typingEl.innerText = msg.body;
        // Force layout so probeCharRects can read the wrapped positions.
        void typingEl.clientHeight;
        spawnMessageBodies(typingEl, world.engine, msg);
        await new Promise<void>((r) => setTimeout(r, gapMs));
      }
      if (!stopped) {
        typingEl.classList.remove('reviving');
        typingEl.innerHTML = '';
        typingEl.contentEditable = 'true';
      }
    }
  } catch (err) {
    // Hydration failure shouldn't break the view — the user can still post.
    console.warn('Failed to hydrate jar messages', err);
    typingEl.classList.remove('reviving');
    typingEl.innerHTML = '';
    typingEl.contentEditable = 'true';
  }

  if (stopped) return;
  typingBox.focus();
  showHint(HINT_INITIAL, 6000);
}
